import { Component, ViewChild } from '@angular/core';
import { environment } from '@environments/environment';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CurrentUser } from '@shared/interfaces';
import { CurrentUserService } from '@core/services';

interface AppNavigation {
    id: string;
    name: string;
    url: string;
    internalRoute?: boolean;
    submenu?: AppNavigation[];
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
    @ViewChild('ovAccounts') ovAccounts: OverlayPanel | null = null;
    @ViewChild('ovAssays') ovAssays: OverlayPanel | null = null;
    @ViewChild('ovOrders') ovOrders: OverlayPanel | null = null;
    @ViewChild('ovPricing') ovPricing: OverlayPanel | null = null;
    @ViewChild('ovProbes') ovProbes: OverlayPanel | null = null;
    @ViewChild('ovPm') ovPm: OverlayPanel | null = null;
    @ViewChild('ovSales') ovSales: OverlayPanel | null = null;
    @ViewChild('ovShipping') ovShipping: OverlayPanel | null = null;
    @ViewChild('ovStrains') ovStrains: OverlayPanel | null = null;
    @ViewChild('ovTagCenter') ovTagCenter: OverlayPanel | null = null;

    currentUser: CurrentUser | null = null;

    ovAccountsOpened = false;
    ovAssaysOpened = false;
    ovOrdersOpened = false;
    ovPricingOpened = false;
    ovProbesOpened = false;
    ovPmOpened = false;
    ovSalesOpened = false;
    ovShippingOpened = false;
    ovStrainsOpened = false;
    ovTagCenterOpened = false;

    accountsSubmenu: AppNavigation[] = [
        {
            id: 'accounts',
            name: 'Accounts',
            url: environment.omAccountsUrl,
        },
        {
            id: 'accounts-organizations',
            name: 'Organizations',
            url: environment.omAccountsOrganizationsUrl,
        },
        {
            id: 'accounts-online-users',
            name: 'Online Users',
            url: environment.omAccountsOnlineUsersUrl,
        },
    ];

    assaysSubmenu: AppNavigation[] = [
        {
            id: 'assays-dashboard',
            name: 'Dashboard',
            url: environment.omAssaysUrl,
        },
        {
            id: 'assays-search',
            name: 'Search',
            url: environment.omAssaysSearchUrl,
        },
        {
            id: 'assays-sequencing',
            name: 'Sequencing',
            url: environment.omAssaysSequencingUrl,
        },
        {
            id: 'assays-probe-mfg',
            name: 'Probe Mfg.',
            url: environment.omAssaysProbesMfgUrl,
        },
        {
            id: 'assays-incomplete-sif',
            name: 'Incomplete SIFs',
            url: environment.omAssaysIncompleteSIFsUrl,
        },
        {
            id: 'assays-constructs',
            name: 'Constructs',
            url: environment.omAssaysConstructsUrl,
        },
    ];

    ordersSubmenu: AppNavigation[] = [
        {
            id: 'orders-order-search',
            name: 'Order Search',
            url: environment.omOrdersUrl,
        },
        {
            id: 'orders-create-orders',
            name: 'Create/Modify Order',
            url: environment.omOrdersCreateUrl,
        },
        {
            id: 'orders-pending-orders',
            name: 'Pending Orders',
            url: environment.omOrdersPendingOrdersUrl,
        },
        {
            id: 'orders-incomplete-orders',
            name: 'Incomplete Orders',
            url: environment.omOrdersIncompleteOrdersUrl,
        },
        {
            id: 'orders-wellplate-storage',
            name: 'Wellplate Storage',
            url: environment.omOrdersWellplateStorageUrl,
        },
    ];

    pricingSubmenu: AppNavigation[] = [
        {
            id: 'pricing-overview',
            name: 'Overview',
            url: environment.omPricingUrl,
        },
        {
            id: 'pricing-promo-credits',
            name: 'Promo/Credits',
            url: environment.omPricingPromoCreditsUrl,
        },
        {
            id: 'pricing-payment-methods',
            name: 'Payment Methods',
            url: environment.omPricingPaymentMethodsUrl,
        },
    ];

    probesSubmenu: AppNavigation[] = [
        {
            id: 'probes-overview',
            name: 'Overview',
            url: environment.omProbesUrl,
        },
        {
            id: 'probes-add-predefined-probe',
            name: 'Add Predefined Probe',
            url: environment.omProbesAddPredefinedProbesUrl,
        },
        {
            id: 'probes-abi-file-import',
            name: 'ABI File Import',
            url: environment.omProbesABIFileImportUrl,
        },
        {
            id: 'probes-manufacturing',
            name: 'Manufacturing',
            url: environment.omProbesManufacturingUrl,
        },
        {
            id: 'probes-sequencing',
            name: 'Sequencing',
            url: environment.omProbesSequencingUrl,
        },
        {
            id: 'probes-incompatible-probes',
            name: 'Incompatible Probes',
            url: environment.omProbesIncompatibleProbesUrl,
        },
    ];

    pmSubmenu: AppNavigation[] = [
        {
            id: 'pm-overview',
            name: 'Overview',
            url: environment.omPmUrl,
        },
        {
            id: 'pm-tasks',
            name: 'Tasks',
            url: environment.omPmTasksUrl,
        },
        {
            id: 'pm-administration-notes',
            name: 'Notes',
            url: environment.omPmNotesUrl,
        },
        {
            id: 'pm-orders-lims',
            name: 'Orders by LIMS Status',
            url: environment.omPmOrdersLimsUrl,
        },
        {
            id: 'pm-pending-orders',
            name: 'Pending Orders',
            url: environment.omPmPendingOrdersUrl,
        },
        {
            id: 'pm-offline-wellplates',
            name: 'Offline Wellplates',
            url: environment.omPmOfflineWellplatesUrl,
        },
        {
            id: 'pm-offline-sequencing',
            name: 'Offline Sequencing',
            url: environment.omPmOfflineSequencingUrl,
        },
        {
            id: 'pm-strain-copy',
            name: 'Strain Copy',
            url: environment.omPmStrainCopyUrl,
        },
        {
            id: 'pm-unreceived-orders',
            name: 'Unreceived Orders',
            url: environment.omPmUnreceivedOrdersUrl,
        },
    ];

    salesSubmenu: AppNavigation[] = [
        {
            id: 'sales-dashboard',
            name: 'Dashboard',
            url: environment.omSalesUrl,
        },
        {
            id: 'sales-assay-design',
            name: 'Assay Design',
            url: environment.omSalesAssayDesignUrl,
        },
        {
            id: 'sales-administration-notes',
            name: 'Notes',
            url: environment.omSalesNotesUrl,
        },
        {
            id: 'sales-prospects',
            name: 'Prospects',
            url: environment.omSalesProspectsUrl,
        },
    ];

    shippingSubmenu: AppNavigation[] = [
        {
            id: 'shipping-supply-requests',
            name: 'Supply Requests',
            url: environment.omShippingUrl,
        },
        {
            id: 'shipping-central-shipping',
            name: 'Central Shipping',
            url: environment.omShippingCentralShippingUrl,
        },
        {
            id: 'shipping-sequencing',
            name: 'Sequencing',
            url: environment.omShippingSequencingUrl,
        },
        {
            id: 'shipping-label-settings',
            name: 'Label Settings',
            url: environment.omShippingLabelSettingsUrl,
        },
        {
            id: 'shipping-manage-pickups',
            name: 'Manage Pickups',
            url: environment.omManagePickupsUrl,
        },
    ];

    strainsSubmenu: AppNavigation[] = [
        {
            id: 'strains-strain-search',
            name: 'Strain Search',
            url: environment.omStrainsUrl,
        },
    ];

    tagCenterSubmenu: AppNavigation[] = [
        {
            id: 'tag-center-contact-manager',
            name: 'Contact Manager',
            url: environment.omTagCenterUrl,
        },
    ];

    appNavigation: AppNavigation[] = [
        {
            id: 'dashboard',
            name: 'Dashboard',
            url: environment.omDashboardUrl,
        },
        {
            id: 'accounts',
            name: 'Accounts',
            url: environment.omAccountsUrl,
            submenu: this.accountsSubmenu,
        },
        {
            id: 'assays',
            name: 'Assays',
            url: environment.omAssaysUrl,
            submenu: this.assaysSubmenu,
        },
        {
            id: 'orders',
            name: 'Orders',
            url: environment.omOrdersUrl,
            submenu: this.ordersSubmenu,
        },
        {
            id: 'pricing',
            name: 'Pricing',
            url: environment.omPricingUrl,
            submenu: this.pricingSubmenu,
        },
        {
            id: 'probes',
            name: 'Probes',
            url: environment.omProbesUrl,
            submenu: this.probesSubmenu,
        },
        {
            id: 'pm',
            name: 'PM',
            url: environment.omPmUrl,
            submenu: this.pmSubmenu,
        },
        {
            id: 'sales',
            name: 'Sales',
            url: environment.omSalesUrl,
            submenu: this.salesSubmenu,
        },
        {
            id: 'shipping',
            name: 'Shipping',
            url: environment.omShippingUrl,
            submenu: this.shippingSubmenu,
        },
        {
            id: 'ckm',
            name: 'CheckMate',
            url: 'checkmate',
            internalRoute: true,
        },
        {
            id: 'strains',
            name: 'Strains',
            url: environment.omStrainsUrl,
            submenu: this.strainsSubmenu,
        },
        {
            id: 'tag-center',
            name: 'TAGCenter',
            url: environment.omTagCenterUrl,
            submenu: this.tagCenterSubmenu,
        },
    ];

    constructor(private currentUserService: CurrentUserService) {
        this.currentUser = this.currentUserService.getCurrentUser();
        this.currentUserService.currentUser$.subscribe(user => {
            this.currentUser = user;
        });
    }

    navItemHover(navItemId: string, event: any): void {
        this.hideAllOverlays();

        if (navItemId === 'accounts') {
            this.ovAccounts?.show(event);
            this.ovAccountsOpened = true;
        } else if (navItemId === 'assays') {
            this.ovAssays?.show(event);
            this.ovAssaysOpened = true;
        } else if (navItemId === 'orders') {
            this.ovOrders?.show(event);
            this.ovOrdersOpened = true;
        } else if (navItemId === 'pricing') {
            this.ovPricing?.show(event);
            this.ovPricingOpened = true;
        } else if (navItemId === 'probes') {
            this.ovProbes?.show(event);
            this.ovProbesOpened = true;
        } else if (navItemId === 'pm') {
            this.ovPm?.show(event);
            this.ovPmOpened = true;
        } else if (navItemId === 'sales') {
            this.ovSales?.show(event);
            this.ovSalesOpened = true;
        } else if (navItemId === 'shipping') {
            this.ovShipping?.show(event);
            this.ovShippingOpened = true;
        } else if (navItemId === 'strains') {
            this.ovStrains?.show(event);
            this.ovStrainsOpened = true;
        } else if (navItemId === 'tag-center') {
            this.ovTagCenter?.show(event);
            this.ovTagCenterOpened = true;
        } else {
            this.hideAllOverlays();
        }
    }

    hideAllOverlays(): void {
        this.ovAccounts?.hide();
        this.ovAccountsOpened = false;

        this.ovAssays?.hide();
        this.ovAssaysOpened = false;

        this.ovOrders?.hide();
        this.ovOrdersOpened = false;

        this.ovPricing?.hide();
        this.ovPricingOpened = false;

        this.ovProbes?.hide();
        this.ovProbesOpened = false;

        this.ovPm?.hide();
        this.ovPmOpened = false;

        this.ovSales?.hide();
        this.ovSalesOpened = false;

        this.ovShipping?.hide();
        this.ovShippingOpened = false;

        this.ovStrains?.hide();
        this.ovStrainsOpened = false;

        this.ovTagCenter?.hide();
        this.ovTagCenterOpened = false;
    }
}
